import { Helmet } from "react-helmet-async";
import { useParams, Navigate } from "react-router-dom";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import PostCard from "@/components/blog/PostCard";
import Breadcrumbs from "@/components/blog/Breadcrumbs";
import NewsletterInline from "@/components/blog/NewsletterInline";
import AppDownloadCTA from "@/components/blog/AppDownloadCTA";
import StickyMobileAppBar from "@/components/blog/StickyMobileAppBar";
import { CATEGORIES, POSTS, type CategorySlug } from "@/content/posts";
import { SITE_URL } from "@/constants/storeLinks";

const BlogCategory = () => {
  const { slug } = useParams<{ slug: string }>();
  const category = slug && CATEGORIES[slug as CategorySlug];
  if (!category) return <Navigate to="/blog" replace />;

  const posts = POSTS.filter((p) => p.category === category.slug);
  const canonical = `${SITE_URL}/blog/category/${category.slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} — PieFolio AI Blog`,
    url: canonical,
    description: category.description,
  };

  return (
    <>
      <Helmet>
        <title>{`${category.name} — PieFolio AI Blog`}</title>
        <meta name="description" content={category.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={`${category.name} — PieFolio AI Blog`} />
        <meta property="og:description" content={category.description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 md:pt-32 pb-20 px-6 md:px-8">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Blog", to: "/blog" },
              { label: category.name },
            ]}
          />

          <header className="mt-6 mb-12 max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-widest text-primary font-semibold mb-3">
              Category
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight">
              {category.name}
            </h1>
            <p className="mt-5 text-lg text-foreground/70 leading-relaxed">
              {category.description}
            </p>
          </header>

          <section className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <PostCard key={p.slug} post={p} />
            ))}
          </section>

          <div className="mt-16">
            <NewsletterInline source={`category-${category.slug}`} />
          </div>

          <div className="mt-12">
            <AppDownloadCTA source={`category-${category.slug}`} />
          </div>
        </div>
      </main>

      <Footer />
      <StickyMobileAppBar />
    </>
  );
};

export default BlogCategory;
